import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  Clock,
  Database,
  ShieldCheck,
} from 'lucide-react';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Banner } from '../../components/Banner';

/**
 * Self-service legacy → dedicated DB migration — §7.7
 * Full-screen confirmation form, requires typing MIGRATE to enable submit.
 */
export function DbMigration() {
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const ready = confirm.trim().toUpperCase() === 'MIGRATE';

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!ready) return;
    setSubmitting(true);
    setTimeout(() => navigate({ to: '/subscription' }), 1100);
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-2xl mx-auto">
      <div className="mb-3">
        <Link
          to="/subscription"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
        >
          <ArrowLeft className="w-4 h-4" /> Back to subscription
        </Link>
      </div>

      <div className="mb-8">
        <p className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-1.5">
          Account
        </p>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
          Move to your own database
        </h1>
        <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
          Available now you're on Pro. We'll copy all data to a new private database without
          interrupting your POS.
        </p>
      </div>

      <Banner tone="info" className="mb-5">
        Your POS keeps working from the legacy database during migration. Cutover is a
        one-second swap and you can revert any time within 30 days.
      </Banner>

      <Card className="p-5 sm:p-7 mb-5">
        <h2 className="text-base font-semibold text-zinc-950 dark:text-zinc-50 mb-4">
          Migration plan
        </h2>

        <div className="grid sm:grid-cols-2 gap-3 mb-6">
          <div className="rounded-lg border border-zinc-200 dark:border-zinc-600 p-4">
            <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-1.5">
              Currently
            </div>
            <div className="flex items-center gap-2">
              <Database className="w-4 h-4 text-zinc-400" />
              <span className="font-medium text-zinc-900 dark:text-zinc-100">
                Shared (legacy) database
              </span>
            </div>
            <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
              ogrogoti_master · shared by all legacy businesses
            </p>
          </div>
          <div className="rounded-lg border-2 border-brand bg-brand-subtle dark:border-brand-dm dark:bg-brand-dm-subtle/30 p-4">
            <div className="text-xs font-medium uppercase tracking-wider text-brand dark:text-brand-dm mb-1.5">
              After migration
            </div>
            <div className="flex items-center gap-2">
              <Database className="w-4 h-4 text-brand dark:text-brand-dm" />
              <span className="font-medium text-zinc-900 dark:text-zinc-100">
                Private managed database
              </span>
            </div>
            <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-300">
              Dedicated · backups · monitoring
            </p>
          </div>
        </div>

        <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <Stat icon={<Clock className="w-3.5 h-3.5" />} label="Estimated time" value="5–15 minutes" />
          <Stat label="Data size" value="~412 MB · 1.2M rows" />
          <Stat label="Downtime" value="~1 second (cutover)" />
          <Stat label="Rollback window" value="30 days" />
        </dl>
      </Card>

      <Card className="p-5 sm:p-7 mb-5">
        <h2 className="text-base font-semibold text-zinc-950 dark:text-zinc-50 mb-3 flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          Safety
        </h2>
        <ul className="space-y-2.5 text-sm text-zinc-700 dark:text-zinc-200">
          <Bullet>We verify row counts and checksums before cutover.</Bullet>
          <Bullet>Your data stays on the legacy DB for 30 days as a rollback.</Bullet>
          <Bullet>You can revert with one click during that window.</Bullet>
          <Bullet>POS keeps working throughout — no data loss possible.</Bullet>
        </ul>
      </Card>

      <Card className="p-5 sm:p-7">
        <form onSubmit={onSubmit}>
          <h2 className="text-base font-semibold text-zinc-950 dark:text-zinc-50 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            Confirm
          </h2>
          <p className="text-sm text-zinc-700 dark:text-zinc-200 mb-4">
            Type{' '}
            <code className="mono px-1 py-0.5 rounded bg-zinc-100 dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 text-xs font-semibold">
              MIGRATE
            </code>{' '}
            below to start the background job.
          </p>

          <input
            type="text"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Type MIGRATE"
            className="block w-full px-3 py-2.5 text-sm rounded-md mono bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand dark:focus:ring-brand-dm"
          />

          <div className="mt-5 pt-5 border-t border-zinc-200 dark:border-zinc-700 flex items-center justify-between gap-3">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate({ to: '/subscription' })}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!ready}
              loading={submitting}
              iconRight={!submitting ? <ArrowRight className="w-4 h-4" /> : undefined}
            >
              Start migration
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

function Stat({
  icon,
  label,
  value,
}: {
  icon?: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400 flex items-center gap-1.5 mb-0.5">
        {icon} {label}
      </dt>
      <dd className="text-sm font-medium text-zinc-900 dark:text-zinc-100 tabular-nums">
        {value}
      </dd>
    </div>
  );
}

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2.5">
      <Check className="w-4 h-4 mt-0.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
      <span>{children}</span>
    </li>
  );
}
